import React from 'react'
import { Box, Checkbox, TextField, Typography } from '@mui/material'

import { Button } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import type { UseWherebyRerunResult } from '../hooks/useWherebyRerun'

interface WherebyRerunToolProps {
  hook: UseWherebyRerunResult
}

/**
 * Dev tool — search teams, multi-select, then rerun the whereby task pipeline
 * for them (with dry-run + include-completed flags).
 */
export const WherebyRerunTool: React.FC<WherebyRerunToolProps> = ({ hook }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          marginBottom: '8px'
        }}
      >
        Rerun Whereby sessions by team
      </Typography>

      <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          value={hook.query}
          onChange={(e) => hook.setQuery(e.target.value)}
          placeholder="Search team name or id"
          sx={{
            minWidth: { xs: '100%', sm: 360 },
            '& .MuiInputBase-root': { color: INTERAX_COLORS.white },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: INTERAX_COLORS.thunderGrey20 }
          }}
        />
        <Button variant="secondary" onClick={hook.loadTeams}>
          {hook.isLoading ? 'Loading…' : 'Search'}
        </Button>
        <Button
          variant="primary"
          disabled={hook.isLoading || hook.selectedTeamIds.length === 0}
          onClick={hook.runRerun}
        >
          Rerun selected ({hook.selectedTeamIds.length})
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={hook.dryRun}
            onChange={(e) => hook.setDryRun(e.target.checked)}
            sx={{
              color: INTERAX_COLORS.thunderGrey,
              '&.Mui-checked': { color: INTERAX_COLORS.sun }
            }}
          />
          <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey }}>Dry run (do not reset)</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={hook.includeCompleted}
            onChange={(e) => hook.setIncludeCompleted(e.target.checked)}
            sx={{
              color: INTERAX_COLORS.thunderGrey,
              '&.Mui-checked': { color: INTERAX_COLORS.sun }
            }}
          />
          <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey }}>
            Include completed tasks (force reprocess)
          </Typography>
        </Box>
      </Box>

      {hook.error && <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.sun, mb: 1 }}>{hook.error}</Typography>}

      {hook.result && (
        <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey, mb: 1 }}>{hook.result}</Typography>
      )}

      <Box
        sx={{
          maxHeight: 260,
          overflow: 'auto',
          borderRadius: '8px',
          border: `1px solid ${INTERAX_COLORS.thunderGrey20}`
        }}
      >
        {hook.teams.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey }}>No teams found.</Typography>
          </Box>
        ) : (
          hook.teams.map((team) => (
            <Box
              key={team.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                p: 2,
                borderBottom: `1px solid ${INTERAX_COLORS.thunderGrey20}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <Checkbox
                  checked={hook.selectedTeamIds.includes(team.id)}
                  onChange={() => hook.toggleTeam(team.id)}
                  sx={{
                    color: INTERAX_COLORS.thunderGrey,
                    '&.Mui-checked': { color: INTERAX_COLORS.sun }
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '14px', fontWeight: 600 }}>
                    {team.name}
                  </Typography>
                  <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '12px' }}>
                    {team.id} • roomNames: {team.wherebyRoomNames?.length ?? 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
}
